import type { VoidFunctionComponent } from 'react';
import { useState } from 'react';
import Header from '../../../shared/settings/SettingsPageHeader';
import Section from '../../../shared/settings/SettingsPageSection';
import ProjectNotificationsSettings from '../../../shared/settings/ProjectNotificationsSettings';
import { useNotifications } from './hooks';
import { window } from '../../../../globals';
import PropagateNotificationModal from './PropagateNotificationModal';
import Loader from '../../../generic/Loader';
import { shouldDisplayProjectNotificationSettings } from '../../shared/permissions';
import OrganizationSettingsPermissionGate from '../../OrganizationSettingsPermissionGate';

/**
 * SettingsOrganizationProjectNotifications is the settings page for managing
 * the default project notification settings.
 */
const SettingsOrganizationProjectNotifications: VoidFunctionComponent = () => {
  const {
    settings,
    users,
    isSaving,
    initializeCustomUsers,
    onSaveSettings,
    isSaveButtonDisabled,
    isEmailNotificationsEnabled,
    updateUsersToCurrent,
    disableEmailNotifications,
    updateUsersToCustom,
    updateUsersToAll,
    onUpdateSettings,
    isLoading,
  } = useNotifications(window.user?.organizationId as number);
  const [shouldDisplayPropagateModal, setShouldDisplayPropagateModal] = useState<boolean>(false);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <Header heading="Default Notification Settings">
        <Header.SaveButton onClick={onSaveSettings} isDisabled={isSaveButtonDisabled} isLoading={isSaving} />
      </Header>
      <Section>
        <Section.Left heading="New Issues">
          <Section.Paragraph>Configure when and where notifications are sent.</Section.Paragraph>
          <Section.PropagateButton
            onClick={() => setShouldDisplayPropagateModal(true)}
            isDisabled={!isSaveButtonDisabled}
          />
        </Section.Left>
        <Section.Right>
          <ProjectNotificationsSettings
            notificationDefaultEnabled={settings?.notificationDefaultEnabled || false}
            onUpdateNotificationDefaultEnabled={(isChecked) =>
              onUpdateSettings({ notificationDefaultEnabled: isChecked })}
            isEmailNotificationsEnabled={isEmailNotificationsEnabled}
            onIsEmailNotificationsEnabledChange={(isChecked) => {
              if (isChecked) {
                // set default users to current user if email notifications is enabled for the first time
                updateUsersToCurrent();
              } else {
                disableEmailNotifications();
              }
            }}
            notificationDefaultEmailScanUserType={settings?.notificationDefaultEmailScanUserType}
            onSelectUsersDropdownChange={(userType) => {
              if (userType === 'current') {
                updateUsersToCurrent();
              } else if (userType === 'all') {
                updateUsersToAll();
              } else {
                // set custom users to the current user if custom users is selected for the first time
                initializeCustomUsers();
              }
            }}
            notificationDefaultSlackScan={settings?.notificationDefaultSlackScan || false}
            onToggleSlackNotifications={(notificationDefaultSlackScan: boolean) =>
              onUpdateSettings({ notificationDefaultSlackScan })}
            users={users || []}
            notificationDefaultEmailScanUsers={settings?.notificationDefaultEmailScanUsers || []}
            onConfirmCustomUserSelection={updateUsersToCustom}
            notificationDefaultApiWebhookScan={settings?.notificationDefaultApiWebhookScan || false}
            onToggleApiWebhookNotifications={(notificationDefaultApiWebhookScan: boolean) =>
              onUpdateSettings({ notificationDefaultApiWebhookScan })}
          />
        </Section.Right>
      </Section>
      {settings && shouldDisplayPropagateModal && (
        <PropagateNotificationModal
          isOpen
          settings={settings}
          onHide={() => setShouldDisplayPropagateModal(false)}
        />
      )}
    </>
  );
};

/**
 * SettingsOrganizationProjectNotificationsContainer displays the project notification settings page if the user has permission to view it.
 */
const SettingsOrganizationProjectNotificationsContainer: VoidFunctionComponent = () => (
  <OrganizationSettingsPermissionGate checkPermission={shouldDisplayProjectNotificationSettings}>
    <SettingsOrganizationProjectNotifications />
  </OrganizationSettingsPermissionGate>
);

export default SettingsOrganizationProjectNotificationsContainer;
